import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import type {LoadingStates} from '@Utils/loading.util';
import type {PromptBoxCoordinatorActions} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import type {ReactNode} from 'react';

type ChatId = string;
type ImageHashedId = string;

export interface GeneratedImagePartActionsLoading {
  downloadLoadingState: LoadingStates;
  openEditorLoadingState: LoadingStates;
  shareLoadingState: LoadingStates;
  saveToMyDesignsLoadingState: LoadingStates;
  loadingPublishingOption: PublishingOption | null;
  publishingLoadingState: LoadingStates;
}

export type GeneratedImagePartActionsLoadingStatesMap = Record<ChatId, Record<ImageHashedId, GeneratedImagePartActionsLoading>>;

export interface AiChatContextType {
  state: {
    hasMessages: boolean;
    currentChatId?: string;
    isSidebarExpanded: boolean;
    chatHistory: HistoryItem[];
    recentChatHistory: HistoryItem[];
    isSmall: boolean;
    isPromptBoxFocused: boolean;
    hasMoreHistory: boolean;
    searchQuery: string | undefined;
    shouldFocusSearch: boolean;
    credits: number;
    isUserLoggedIn: boolean;
    getLoadingStatesForImageActions: (chatId: string, imageHashedId: string) => GeneratedImagePartActionsLoading;
    isSuperUser: boolean;
    showDeletedMessages: boolean;
    editingUserMessageIdMap: Record<string, string | null>;
    isPaygUserWithAiChatAccess: boolean;
    isReturningUser: boolean;
    landingTipRefreshKey: number;
    isQuickTipsView: boolean;
    isChatHistorySearchLoading: boolean;
  };
  actions: {
    setCurrentChatId: (chatId: string | undefined) => void;
    setCurrentChatIdInternal: (chatId: string | undefined) => void;
    setIsSidebarExpanded: (expanded: boolean) => void;
    setSearchQuery: (query: string | undefined) => void;
    setShouldFocusSearch: (value: boolean) => void;
    setIsPromptBoxFocused: (focused: boolean) => void;
    refetchChatHistory: () => Promise<void>;
    fetchChatHistory: (searchQuery: string | undefined, offset: number, limit: number) => Promise<HistoryItem[]>;
    refreshCredits: () => void;
    onLoginSuccessActions: () => void;
    updateLoadingStatesForImageActions: (chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>) => void;
    updateChatHistoryItem: (chatId: string, updates: Partial<HistoryItem>) => void;
    disconnectChatStream: (chatId: string) => void;
    registerDisconnectChatStream: (disconnectFn: (chatId: string) => void) => void;
    deleteChatHistoryItem: (chatId: string) => void;
    setIsPaygUserWithAiChatAccess: (value: boolean) => void;
    setEditingUserMessageId: (messageId: string | null, chatId?: string) => void;
    setShowDeletedMessages: (show: boolean) => void;
    activateForNewChat: () => void;
    activatePromptBox: () => void;
    registerPromptBoxCoordinator: (actions: PromptBoxCoordinatorActions | null) => void;
    navigateToQuickTips: () => void;
    navigateBack: () => void;
  };
}

export interface AiChatProviderProps {
  children: ReactNode;
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
}

export const AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE = 'pmwacsec';
